import { useMemo, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { DRAFT_COLUMN, MOBILE_SCREENS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { useAllDrafts, useMobileScreens } from '@mobileHooks'
import { ConfigListPageShell } from '@mobileComponents/ConfigListPageShell'
import { MobileScreen } from '@mobileTypes'
import { Add as AddIcon } from '@mui/icons-material'
import { Box, Button } from '@mui/material'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileScreens = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllScreens = matchPath(ROUTES.CONFIG_MOBILE_SCREENS, pathname)

  const { screens, loading, copyScreen, deleteScreen } = useMobileScreens()
  const { entries: draftEntries, deleteDraft } = useAllDrafts<MobileScreen>('mobile-screen-draft-')

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [screenToCopy, setScreenToCopy] = useState<{
    id: number
    title: string
    internalId: string
  } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [screenToDelete, setScreenToDelete] = useState<{ id: number; title: string } | null>(null)

  const handleCreate = () => navigate(ROUTES.CONFIG_MOBILE_SCREENS_CREATE)

  const handleEdit = (id: number) => navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${id}`)

  const handleCopyClick = (id: number) => {
    const screen = screens.find(s => s.id === id)
    if (screen) {
      setScreenToCopy({ id: screen.id, title: screen.title, internalId: screen.internalId })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = async (newTitle: string, newInternalId: string) => {
    if (screenToCopy) {
      const newScreen = await copyScreen(screenToCopy.id, newTitle, newInternalId)
      if (newScreen) {
        setCopyDialogOpen(false)
        setScreenToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${newScreen.id}`)
      }
    }
  }

  const handleDeleteClick = (id: number) => {
    const screen = screens.find(s => s.id === id)
    if (screen) {
      setScreenToDelete({ id: screen.id, title: screen.title })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = async () => {
    if (screenToDelete) {
      await deleteScreen(screenToDelete.id)
      setDeleteDialogOpen(false)
      setScreenToDelete(null)
    }
  }

  const handleExport = (id: number) => {
    const screen = screens.find(s => s.id === id)
    if (screen) exportToJSON(screen, `${screen.title}_config.json`)
  }

  const rows = useMemo(() => {
    const draftEntityIdMap = new Map(
      draftEntries.filter(e => e.idPart !== 'new').map(e => [e.idPart, e])
    )

    const newDraftRows = draftEntries
      .filter(e => e.idPart === 'new')
      .map(e => ({
        ...e.draft.data,
        id: 'draft-new',
        _isDraft: true,
        _isNewDraft: true,
        _draftKey: e.key,
      }))

    const entityDraftRows = screens
      .filter(s => draftEntityIdMap.has(String(s.id)))
      .map(s => {
        const entry = draftEntityIdMap.get(String(s.id))!

        return {
          ...s,
          ...entry.draft.data,
          id: s.id,
          _isDraft: true,
          _isNewDraft: false,
          _draftKey: entry.key,
        }
      })

    const serverRows = screens.filter(s => !draftEntityIdMap.has(String(s.id)))

    return [...newDraftRows, ...entityDraftRows, ...serverRows]
  }, [screens, draftEntries])

  const columns = [
    DRAFT_COLUMN,
    ...MOBILE_SCREENS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: (params: any) => {
        if (params.row._isNewDraft) {
          navigate(ROUTES.CONFIG_MOBILE_SCREENS_CREATE, { state: { restoreDraft: true } })
        } else if (params.row._isDraft) {
          navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${params.row.id}`, {
            state: { restoreDraft: true },
          })
        } else {
          handleEdit(params.row.id)
        }
      },
      onCopy: (params: any) => {
        if (!params.row._isNewDraft) handleCopyClick(params.row.id)
      },
      onDelete: (params: any) => {
        if (params.row._isDraft) {
          deleteDraft(params.row._draftKey)
        } else {
          handleDeleteClick(params.row.id)
        }
      },
      onDowloadButtonClick: (params: any) => {
        if (!params.row._isNewDraft) handleExport(params.row.id)
      },
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy Mobile Screen'
        initialName={screenToCopy?.title || ''}
        existingNames={screens.map(s => s.title)}
        initialInternalId={screenToCopy?.internalId}
        existingInternalIds={screens.map(s => s.internalId)}
        onClose={() => {
          setCopyDialogOpen(false)
          setScreenToCopy(null)
        }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete Mobile Screen'
        message='Are you sure you want to delete screen'
        itemName={screenToDelete?.title}
        onClose={() => {
          setDeleteDialogOpen(false)
          setScreenToDelete(null)
        }}
        onConfirm={handleDelete}
      />

      <ConfigListPageShell
        title='Mobile Screens'
        isVisible={!!isAllScreens}
        actions={
          <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        }
      >
          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              hideFooter
              disableRowSelectionOnClick
            />
          </Box>
      </ConfigListPageShell>
    </>
  )
}
